import { ExportToCsv } from 'export-to-csv-fix-source-map';
import * as accounts from '../xhr/accounts.js';
import * as types from './types/accounts.js';
import * as advisorTypes from './types/advisors.js';
import * as quotesTypes from './types/quotes.js';
import * as notifier from './notifier.js';
import store from '../redux/store.js';
import * as dates from "@simplybenefits/sb-dates";
import { i18n, tokens } from '@simplybenefits/sb-ui-library';
import * as loader from './loader.js';

/**
 * @param dispatch
 * @param isLoading
 * @param type
 */
function setLoader(dispatch, isLoading, type = 'full') {
    dispatch(loader.setLoader({
        isLoading,
        type
    }));
}

/**
 *
 * @param {*} query
 * @param {*} nextCursor
 */
export function getAccounts(query, nextCursor = false) {
    return (dispatch) => {
        const cursor = nextCursor === false ? store.getState().accounts.cursor : Number(nextCursor);
        if (cursor < 0) {
            return false;
        }

        return accounts
            .getAll(cursor, query)
            .then((data) => {
                dispatch({
                    type: types.GET_ACCOUNTS_SUCCESS,
                    accounts: data.accounts,
                    cursor: data.accounts.length === 0 ? -1 : data.cursor,
                    shouldAppend: cursor > 0
                });

                return data;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param query
 * @param nextCursor
 * @param status
 * @returns {Function}
 */
export function getAccountsSpecific(query = '', nextCursor = false, status = 1) {
    return (dispatch) => {
        const cursor = nextCursor === false ? store.getState().advisors.currentAdvisor.cursor : Number(nextCursor);
        if (cursor < 0) {
            return false;
        }

        const advisorID = store.getState().advisors.currentAdvisor.advisorID;

        return accounts
            .getSpecific(query, cursor, status, advisorID)
            .then((data) => {
                dispatch({
                    type: advisorTypes.GET_SPECIFIC_ADVISOR_ACCOUNTS_SUCCESS,
                    accounts: data.accounts,
                    cursor: data.accounts.length === 0 ? -1 : data.cursor,
                    shouldAppend: cursor > 0
                });

                return true;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 *
 * @param accountID
 * @param statuses
 * @returns {function(*): Promise<unknown>}
 */
export function getAccountRenewalPeriods(accountID, statuses) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .getRenewalPeriods(accountID, statuses)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return [];
            });
    };
}

/**
 *
 * @param groupNum
 * @returns {function(*): Promise<*>}
 */
export function getAccountByGroupNum(groupNum) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .getAccountByGroupNum(groupNum)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return {};
            });
    };
}

/**
 *
 * @param {*} accountID
 */
export function getAccount(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .get(accountID)
            .then((data) => {
                dispatch({
                    type: types.GET_ACCOUNT_SUCCESS,
                    data
                });

                setLoader(dispatch, false);
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 *
 * @param {*} accountID
 * @param {*} body
 */
export function editAccount(accountID, body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .updateAccount(accountID, body)
            .then(() => {
                dispatch({
                    type: types.UPDATE_ACCOUNT_SUCCESS,
                    accountID,
                    body
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Account updated successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} query
 * @param {*} nextCursor
 */
export function getReviewAccounts(query, nextCursor = false) {
    return (dispatch) => {
        const cursor = nextCursor === false ? store.getState().accounts.forReview.cursor : Number(nextCursor);
        if (cursor < 0) {
            return false;
        }

        return accounts
            .getReviewAccounts(cursor, query)
            .then((data) => {
                dispatch({
                    type: types.GET_REVIEW_ACCOUNTS_SUCCESS,
                    accounts: data.accounts,
                    cursor: data.accounts.length === 0 ? -1 : data.cursor,
                    shouldAppend: cursor > 0
                });

                return true;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param classID
 * @param designID
 * @param benefit
 * @param coverageType
 * @param data
 * @param quoteID
 * @param revisionID
 * @returns {(function(*): void)|*}
 */
export function savePricingInput(classID, designID, benefit, coverageType, data, quoteID = null, revisionID = null) {
    return (dispatch) => {
        if (quoteID) {
            dispatch({
                type: quotesTypes.SAVE_PRICING_INPUT,
                classID,
                designID,
                benefit,
                data,
                coverageType,
                quoteID
            });
        } else {
            dispatch({
                type: types.SAVE_PRICING_INPUT,
                classID,
                designID,
                benefit,
                coverageType,
                data,
                revisionID
            });
        }
    };
}

/**
 * @param classID
 * @param designID
 * @param data
 * @param effectiveDate
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function savePricing(classID, designID, data, effectiveDate = null, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .savePricing(classID, designID, data, quoteID, revisionID, effectiveDate)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Pricing saved successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function submitPricing(accountID, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .submitPricing(accountID, quoteID, revisionID)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Pricing submitted successfully!'));

                if (revisionID) {
                    dispatch({
                        type: types.SET_STATE,
                        state: 3,
                        accountID,
                        revisionID
                    });
                } else {
                    window.location.reload();
                }

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param telusDrugPlanID
 * @param designID
 * @param classID
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function saveTelusDrugPlanID(telusDrugPlanID, designID, classID, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true, Boolean(quoteID));
        return accounts
            .saveTelusDrugPlanID(telusDrugPlanID, designID, quoteID, revisionID)
            .then(() => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.SAVE_TELUS_DRUG_PLAN_ID,
                        classID,
                        designID,
                        telusDrugPlanID
                    });
                } else {
                    dispatch({
                        type: types.SAVE_TELUS_DRUG_PLAN_ID,
                        classID,
                        designID,
                        telusDrugPlanID,
                        revisionID
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Telus PlanID saved successfully!!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param designID
 * @param quoteID
 * @returns {function(*): Promise<* | boolean>}
 */
export function getPricingRevisions(designID, quoteID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts.getPricingRevisions(designID, quoteID)
            .then((revisions) => {
                setLoader(dispatch, false);
                return revisions;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param revisionID
 * @param quoteID
 * @returns {function(*): Promise<* | boolean>}
 */
export function getSinglePricingRevision(revisionID, quoteID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts.getSinglePricingRevision(revisionID, quoteID)
            .then((revision) => {
                setLoader(dispatch, false);
                return revision;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} classID
 * @param {*} data
 */
export function savePlanForClass(classID, data) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .updatePlan(classID, data)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Plan updated successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} classID
 * @param {*} type
 * @param {*} key
 * @param {*} value
 */
export function updatePlanRecord(classID, type, key, value) {
    return (dispatch) => {
        dispatch({
            type: types.UPDATE_PLAN_CLASS_RECORD,
            data: {
                classID,
                type,
                key,
                value
            }
        });
    };
}

/**
 *
 * @param {*} accountID
 * @param {*} data
 */
export function notifyAllEmployees(accountID, data) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .notifyAllUsers(accountID, data)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Notifications sent successfully!'));
                return null;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                if (error.code === 'CREDENTIALS_FAILED') {
                    return error.verbiage;
                }

                dispatch(notifier.displayError(error));
                return null;
            });
    };
}

/**
 *
 * @param {*} userID
 * @param {*} accountID
 * @param {*} data
 */
export function notifyEmployee(userID, accountID, data) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .notifyUser(userID, accountID, data)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Notifications sent successfully!'));
                return null;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                if (error.code === 'CREDENTIALS_FAILED') {
                    return error.verbiage;
                }

                dispatch(notifier.displayError(error));
                return null;
            });
    };
}

/**
 * Add a user to the census data, need to know if its a advisor making the request or not
 * @param data
 * @param accountID
 * @returns {function(*): Promise}
 */
export function addUser(data, accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .addUser(data, accountID)
            .then((response) => {
                const currentDate = dates.getLocalDisplay(new Date(), 'X');

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (data.effectiveDate <= currentDate) {
                    dispatch({
                        type: types.ADD_CENSUS_USER_SUCCESS,
                        data: {
                            ...data,
                            userID: response.userID
                        }
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Member added successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * Update a user to the census data, need to know if its a advisor making the request or not
 * @param data
 * @param userID
 * @param accountID
 * @returns {function(*): Promise}
 */
export function updateUser(data, userID, accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .updateUser(data, userID)
            .then(() => {
                const currentDate = dates.getLocalDisplay(new Date(), 'X');

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (data.effectiveDate <= currentDate) {
                    dispatch({
                        type: types.UPDATE_CENSUS_USER_SUCCESS,
                        data: {
                            ...data,
                            accountID,
                            userID
                        }
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Member updated successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * Remove a user from the census data
 * @param userID
 * @param accountID
 * @param effectiveDate
 * @returns {function(*): Promise}
 */
export function removeUser(userID, accountID, effectiveDate) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .removeUser(userID, accountID, effectiveDate)
            .then(() => {
                const currentDate = dates.getLocalDisplay(new Date(), 'X');

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (effectiveDate <= currentDate) {
                    dispatch({
                        type: types.REMOVE_CENSUS_USER_SUCCESS,
                        userID
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('User removed successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @returns {function(*=): Promise<T | never>}
 */
export function getUsers(accountID, filters) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts.getUsers(accountID, filters)
            .then((users) => {
                dispatch({
                    type: types.GET_USERS_SUCCESS,
                    users
                });

                setLoader(dispatch, false);
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param title
 * @param description
 * @param accountID
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function addClass(title, description, isRetiree, accountID, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .addClass(title, description, isRetiree, accountID, quoteID, revisionID)
            .then((data) => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.ADD_CLASS_SUCCESS,
                        data
                    });

                    dispatch({
                        type: quotesTypes.ADD_DESIGN_FOR_NEW_CLASS,
                        data
                    });
                } else {
                    dispatch({
                        type: types.ADD_CLASS_SUCCESS,
                        data,
                        revisionID
                    });

                    dispatch({
                        type: types.ADD_DESIGN_FOR_NEW_CLASS,
                        data,
                        revisionID
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Class added successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param title
 * @param description
 * @param isRetiree
 * @param earningsDefinition
 * @param classID
 * @param accountID
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function editClass(title, description, isRetiree, earningsDefinition, classID, accountID, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .editClass(title, description, isRetiree, earningsDefinition, classID, accountID, quoteID, revisionID)
            .then(() => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.EDIT_CLASS_SUCCESS,
                        data: {
                            title,
                            description,
                            isRetiree,
                            earningsDefinition,
                            classID
                        }
                    });
                } else {
                    dispatch({
                        type: types.EDIT_CLASS_SUCCESS,
                        data: {
                            title,
                            description,
                            isRetiree,
                            earningsDefinition,
                            classID,
                            revisionID
                        },
                        revisionID
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Class saved successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param title
 * @param description
 * @param address
 * @param bankingDetails
 * @param accountID
 * @param numberOfPrivateLabelLives
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function addDivision(title, description, address, bankingDetails, accountID, numberOfPrivateLabelLives = null, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .addDivision(title, description, address, bankingDetails, accountID, quoteID, revisionID, numberOfPrivateLabelLives)
            .then((data) => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.ADD_DIVISION_SUCCESS,
                        data: {
                            title: data.title,
                            description: data.description,
                            address: data.address,
                            hasBanking: Boolean(data.bankingDetails),
                            divisionID: data.divisionID,
                            numberOfPrivateLabelLives
                        }
                    });
                } else {
                    dispatch({
                        type: types.ADD_DIVISION_SUCCESS,
                        data: {
                            title: data.title,
                            description: data.description,
                            address: data.address,
                            hasBanking: Boolean(data.bankingDetails),
                            divisionID: data.divisionID
                        },
                        revisionID
                    });
                }

                setLoader(dispatch, false);

                setTimeout(() => {
                    window.location.reload();
                }, 500);

                dispatch(notifier.displayMessage('Division added successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param accountID
 * @returns {function(*): Promise<*>}
 */
export function getDivisions(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts.getDivisions(accountID)
            .then((result) => {
                setLoader(dispatch, false);
                return result;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 *
 * @param accountID
 * @returns {function(*): Promise<*>}
 */
export function getClasses(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts.getClasses(accountID)
            .then((result) => {
                setLoader(dispatch, false);
                return result;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param title
 * @param description
 * @param address
 * @param bankingDetails
 * @param accountID
 * @param divisionID
 * @param hasBanking
 * @param numberOfPrivateLabelLives
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function editDivision(title, description, address, bankingDetails, accountID, divisionID, hasBanking, numberOfPrivateLabelLives = null, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .editDivision(title, description, address, bankingDetails, accountID, divisionID, quoteID, revisionID, numberOfPrivateLabelLives)
            .then(() => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.EDIT_DIVISION_SUCCESS,
                        data: {
                            title,
                            description,
                            address,
                            hasBanking: hasBanking || Boolean(bankingDetails),
                            divisionID,
                            numberOfPrivateLabelLives
                        }
                    });
                } else {
                    dispatch({
                        type: types.EDIT_DIVISION_SUCCESS,
                        data: {
                            title,
                            description,
                            address,
                            hasBanking: hasBanking || Boolean(bankingDetails),
                            divisionID
                        },
                        revisionID
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Division saved successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} data
 * @param {*} accountID
 */
export function createAdmin(data, accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .createAdmin(data, accountID)
            .then((response) => {
                dispatch({
                    type: types.CREATE_ADMIN_SUCCESS,
                    data: {
                        ...data,
                        adminID: response.adminID,
                        accountID: accountID,
                        isParent: data.isParent,
                        isPlanSponsor: 0
                    }
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Admin created successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} adminID
 */
export function removeAdmin(adminID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .removeAdmin(adminID)
            .then(() => {
                dispatch({
                    type: types.REMOVE_ADMIN_SUCCESS,
                    adminID
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Admin removed successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} data
 * @param {*} adminID
 */
export function updateAdmin(data, adminID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .updateAdmin(data, adminID)
            .then(() => {
                dispatch({
                    type: types.UPDATE_ADMIN_SUCCESS,
                    data: {
                        ...data,
                        adminID
                    }
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Admin updated successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 * @param quoteID
 * @returns {function(*=): Promise<boolean>}
 */
export function getAccountHistoryKeys(accountID, quoteID = false) {
    return (dispatch) => {
        setLoader(dispatch, true, quoteID);

        return accounts
            .getAccountHistoryKeys(accountID, quoteID)
            .then((data) => {
                setLoader(dispatch, false, quoteID);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false, quoteID);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param form
 * @param type
 * @param accountID
 * @param quoteID
 * @returns {function(*=): Promise<any>}
 */
export function uploadAccountHistory(form, type, accountID, quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.uploadAccountHistory(form, accountID, quoteID)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(`${ type } submitted successfully!`));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                notifier.displayError(error.verbiage);
                return false;
            });
    };
}

/**
 * @param accountID
 * @param quoteID
 * @returns {function(*=): Promise<boolean>}
 */
export function getMasterApplicationData(accountID, quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getMasterApplicationData(accountID, quoteID)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * This function sets the account to say they are done with the master app
 * @param accountID
 * @param quoteID
 * @returns {function(*=): Promise<boolean>}
 */
export function completeMasterApp(accountID, quoteID = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.completeMasterApp(accountID, quoteID)
            .then(() => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.SET_STATE,
                        state: 4,
                        quoteID
                    });
                } else {
                    dispatch({
                        type: types.SET_STATE,
                        state: 4,
                        accountID
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(`Master application submitted successfully!`));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}


/**
 * @param accountID (this is not used but it will be passed in from the parent automatically)
 * @param assignedPlans
 * @param quoteID
 * @param revisionID
 * @returns {function(*=): Promise<boolean>}
 */
export function assignPlans(accountID, assignedPlans, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .assignPlans(accountID, assignedPlans, quoteID, revisionID)
            .then(() => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.SET_STATE,
                        state: 6,
                        quoteID
                    });
                } else {
                    dispatch({
                        type: types.SET_STATE,
                        state: revisionID ? 4 : 7,
                        accountID,
                        revisionID
                    });
                }

                dispatch(notifier.displayMessage(`Plan assignment finished successfully!`));
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 * @param reason
 * @param quoteID
 * @returns {function(*=): Promise<unknown>}
 */
export function rejectPricing(accountID, reason, quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .rejectPricing(accountID, reason, quoteID)
            .then(() => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.SET_STATE,
                        state: 4,
                        quoteID
                    });
                }

                dispatch(notifier.displayMessage(`Pricing rejected successfully!`));
                window.location.reload();

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param data
 * @param showLoader
 * @param showSuccess
 * @param quoteID
 * @returns {function(*=): Promise<boolean>}
 */
export function updateMasterApp(accountID, data, showLoader, showSuccess, quoteID = false) {
    return (dispatch) => {
        if (showLoader) {
            setLoader(dispatch, true);
        }

        return accounts.updateMasterApp(accountID, data, quoteID)
            .then(() => {
                setLoader(dispatch, false);

                if (showLoader && showSuccess) {
                    dispatch(notifier.displayMessage(`Master application saved successfully!`));
                }

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param body
 * @returns {function(*=): Promise<boolean | never>}
 */
export function resetAdminPassword(body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .sendForgotPassword(body)
            .then(() => {
                dispatch(notifier.displayMessage('Inform the admin to check their inbox for a recovery email.'));
                setLoader(dispatch, false);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 */
export function allowEnrollments(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .allowEnrollments(accountID)
            .then(() => {
                dispatch(notifier.displayMessage('Enrollments are now active. The account admins have been notified of the change.'));
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function submitPlanDesigns(accountID, quoteID, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts.submitPlanDesigns(accountID, quoteID, revisionID)
            .then(() => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.SET_STATE,
                        state: 1,
                        quoteID
                    });
                } else {
                    dispatch({
                        type: types.SET_STATE,
                        state: revisionID ? 1 : 2,
                        accountID,
                        revisionID
                    });
                }

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function submitModuleBlueprint(accountID, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts.submitModuleBlueprint(accountID, quoteID, revisionID)
            .then((data) => {
                if (revisionID) {
                    dispatch({
                        type: types.UPDATE_REVISION_MODULAR_CLASSES,
                        classes: data.revision.classes,
                        planDesigns: data.revision.planDesigns
                    });
                }

                if (quoteID) {
                    dispatch({
                        type: quotesTypes.SET_STATE,
                        state: 1,
                        quoteID
                    });
                } else {
                    dispatch({
                        type: types.SET_STATE,
                        state: revisionID ? 1 : 2,
                        accountID,
                        revisionID
                    });
                }

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param classID
 * @param planDesign
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function createPlanDesignFromUpload(classID, planDesign, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.createDesignFromUpload(classID, planDesign, quoteID, revisionID)
            .then((data) => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.CREATE_NEW_PLAN_DESIGN,
                        classID,
                        quoteID,
                        data,
                        designID: data.designID
                    });
                } else {
                    dispatch({
                        type: types.CREATE_NEW_PLAN_DESIGN,
                        classID,
                        data,
                        revisionID
                    });
                }

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param classID
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function createPlanDesign(classID, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.createDesign(classID, quoteID, revisionID)
            .then((data) => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.CREATE_NEW_PLAN_DESIGN,
                        classID,
                        quoteID,
                        data,
                        designID: data.designID
                    });
                } else {
                    dispatch({
                        type: types.CREATE_NEW_PLAN_DESIGN,
                        classID,
                        data,
                        revisionID
                    });
                }

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param classID
 * @param designID
 * @param data
 * @param quoteID
 * @returns {Function}
 */
export function revertToRevision(classID, designID, data, quoteID) {
    return (dispatch) => {
        if (quoteID) {
            dispatch({
                type: quotesTypes.REVERT_FROM_REVISION,
                classID,
                designID,
                data,
                quoteID
            });
        } else {
            dispatch({
                type: types.REVERT_TO_PLAN_DESIGN_REVISION,
                classID,
                designID,
                data
            });
        }
    };
}

/**
 * @param classID
 * @param designID
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function clonePlanDesign(classID, designID, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.cloneDesign(designID, quoteID, revisionID)
            .then((response) => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.CREATE_NEW_PLAN_DESIGN,
                        data: response,
                        designID: response.designID,
                        classID,
                        quoteID
                    });
                } else {
                    dispatch({
                        type: types.CREATE_NEW_PLAN_DESIGN,
                        classID,
                        data: response,
                        revisionID
                    });
                }

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param designID
 * @param data
 * @param effectiveDate
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function savePlanDesign(designID, data, effectiveDate = null, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.saveDesign(designID, data, quoteID, revisionID, effectiveDate)
            .then(() => {
                dispatch(notifier.displayMessage('Plan design saved successfully!'));
                setLoader(dispatch, false);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param classID
 * @param data
 * @param effectiveDate
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<boolean>}
 */
export function saveModularBlueprint(classID, data, effectiveDate = null, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.saveModularBlueprint(classID, data, quoteID, revisionID, effectiveDate)
            .then(() => {
                dispatch(notifier.displayMessage('Modular Flex Blueprint saved successfully!'));
                setLoader(dispatch, false);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param classID
 * @param designID
 * @param benefit
 * @param data
 * @param quoteID
 * @param revisionID
 * @returns {(function(*): void)|*}
 */
export function savePlanDesignBenefitInitData(classID, designID, benefit, data, quoteID = null, revisionID = null) {
    return (dispatch) => {
        if (quoteID) {
            dispatch({
                type: quotesTypes.SAVE_PLAN_DESIGN_BENEFIT_INIT_DATA,
                classID,
                designID,
                benefit,
                data,
                quoteID
            });
        } else {
            dispatch({
                type: types.SAVE_PLAN_DESIGN_BENEFIT_INIT_DATA,
                classID,
                designID,
                benefit,
                data,
                revisionID
            });
        }
    };
}

/**
 * @param classID
 * @param tier
 * @param tierBenefit
 * @param benefit
 * @param field
 * @param value
 * @param quoteID
 * @param revisionID
 * @returns {(function(*): void)|*}
 */
export function saveModularField(classID, tier, tierBenefit, benefit, field, value, quoteID = null, revisionID = null) {
    return (dispatch) => {
        if (quoteID) {
            dispatch({
                type: quotesTypes.SAVE_MODULAR_FIELD,
                classID,
                tier,
                benefit,
                tierBenefit,
                field,
                value,
                quoteID
            });
        } else {
            dispatch({
                type: types.SAVE_MODULAR_FIELD,
                classID,
                tier,
                benefit,
                tierBenefit,
                field,
                value,
                revisionID
            });
        }
    };
}

/**
 * @param classID
 * @param tier
 * @param tierBenefit
 * @param benefit
 * @param data
 * @param quoteID
 * @param revisionID
 * @returns {(function(*): void)|*}
 */
export function saveModularBenefitInitData(classID, tier, tierBenefit, benefit, data, quoteID = null, revisionID = null) {
    return (dispatch) => {
        if (quoteID) {
            dispatch({
                type: quotesTypes.SAVE_MODULAR_BENEFIT_INIT_DATA,
                classID,
                tier,
                tierBenefit,
                benefit,
                data,
                quoteID
            });
        } else {
            dispatch({
                type: types.SAVE_MODULAR_BENEFIT_INIT_DATA,
                classID,
                tier,
                tierBenefit,
                benefit,
                data,
                revisionID
            });
        }
    };
}

/**
 * @param classID
 * @param designID
 * @param benefit
 * @param field
 * @param value
 * @param quoteID
 * @param revisionID
 * @returns {(function(*): void)|*}
 */
export function savePlanDesignField(classID, designID, benefit, field, value, quoteID = null, revisionID = null) {
    return (dispatch) => {
        if (quoteID) {
            dispatch({
                type: quotesTypes.SAVE_PLAN_DESIGN_FIELD,
                classID,
                designID,
                benefit,
                field,
                value,
                quoteID
            });
        } else {
            dispatch({
                type: types.SAVE_PLAN_DESIGN_FIELD,
                classID,
                designID,
                benefit,
                field,
                value,
                revisionID
            });
        }
    };
}

/**
 * @param classID
 * @param designID
 * @param data
 * @returns {Function}
 */
export function revertToPlanDesignRevision(classID, designID, data) {
    return (dispatch) => {
        dispatch({
            type: types.REVERT_TO_PLAN_DESIGN_REVISION,
            classID,
            designID,
            data
        });
    };
}

/**
 * @param designID
 * @param quoteID
 * @returns {function(*=): Promise<boolean>}
 */
export function getPlanDesignRevisions(designID, quoteID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts.getPlanDesignRevisions(designID, quoteID)
            .then((revisions) => {
                setLoader(dispatch, false);
                return revisions;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param revisionID
 * @param quoteID
 * @returns {function(*=): Promise<boolean>}
 */
export function getSinglePlanDesignRevision(revisionID, quoteID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts.getSinglePlanDesignRevision(revisionID, quoteID)
            .then((revision) => {
                setLoader(dispatch, false);
                return revision;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 * @param quoteID
 * @param revisionID
 * @returns {function(*): Promise<*>}
 */
export function getCommissions(accountID, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .getCommissions(accountID, quoteID, revisionID)
            .then((commissions) => {
                setLoader(dispatch, false);
                return commissions;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * Auto save the commissions for the advisor
 * @param accountID
 * @param commissions
 * @param quoteID
 * @param revisionID
 * @param effectiveDate
 * @returns {function(*=): Promise<boolean>}
 */
export function saveCommissions(accountID, commissions, effectiveDate = null, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .saveCommissions(accountID, commissions, quoteID, revisionID, effectiveDate)
            .then((data) => {
                if (data.quoteID) {
                    dispatch({
                        type: quotesTypes.SAVE_COMMISSIONS,
                        commissions
                    });
                } else if (data.revisionID) {
                    dispatch({
                        type: types.SAVE_COMMISSIONS,
                        revisionID,
                        commissions
                    });
                } else if (!data.pendingChgID) { // Live updates completed on the account dashboard
                    dispatch({
                        type: types.SAVE_COMMISSIONS,
                        commissions
                    });
                }

                dispatch(notifier.displayMessage(`Commissions saved successfully!`));

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * Submit the commission and move to next step
 * @param accountID
 * @param commissions
 * @param quoteID
 * @param revisionID
 * @returns {function(*=): Q.Promise<>}
 */
export function submitCommissions(accountID, commissions, quoteID = null, revisionID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .submitCommission(accountID, commissions, quoteID, revisionID)
            .then(() => {
                if (revisionID) {
                    dispatch({
                        type: types.SET_STATE,
                        state: 2,
                        accountID,
                        revisionID
                    });

                    dispatch({
                        type: types.SAVE_COMMISSIONS,
                        revisionID,
                        commissions
                    });
                }

                dispatch(notifier.displayMessage(`Commission set successfully!`));
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @returns {function(*=): Promise<boolean | never>}
 */
export function submitCensus(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts
            .submitCensus(accountID)
            .then(() => {
                dispatch({
                    type: types.SET_STATE,
                    state: 1,
                    accountID
                });

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param accountID
 * @param body
 * @returns {function(*): *}
 */
export function uploadMassUpdateForm(accountID, body) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts.uploadMassUpdateForm(accountID, body)
            .then((result) => {
                setLoader(dispatch, false);
                if (result.isSuccessful === true) {
                    dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.uploadedMassUpdateSuccessfully)));
                }

                if ('errors' in result) {
                    dispatch(notifier.displayError('There were some errors while uploading the file'));
                }

                return result;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param accountID
 * @returns {function(*): Promise<unknown | boolean>}
 */
export function downloadSalaryUpdateData(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true, false);
        return accounts.downloadSalaryUpdateData(accountID)
            .then((data) => {
                const date = new Date().toLocaleDateString();
                const options = {
                    fieldSeparator: ',',
                    filename: i18n.t(tokens.labels.massSalaryDownloadFileName, { date: date }),
                    quoteStrings: '"',
                    decimalSeparator: '.',
                    showLabels: true,
                    showTitle: false,
                    title: `Member Salary and Wage Data - ${ date }`,
                    useTextFile: false,
                    useBom: true,
                    useKeysAsHeaders: true
                };

                const csvExporter = new ExportToCsv(options);
                csvExporter.generateCsv(data);

                setLoader(dispatch, false, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param accountID
 * @param form
 * @returns {function(*): Promise<boolean | void>}
 */
export function uploadMassSalaryForm(accountID, form) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return accounts.uploadMassSalaryForm(accountID, form)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.notifications.usersAddedSuccessfully)));

                setTimeout(() => {
                    window.location.reload();
                }, 1000);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param form
 * @returns {function(*=): Promise<boolean | never>}
 */
export function uploadCensus(form, accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.uploadCensus(form, accountID)
            .then((response) => {
                if (response.failedRecords) {
                    setLoader(dispatch, false);
                    return response;
                }

                dispatch({
                    type: types.GET_CENSUS_SUCCESS,
                    users: response,
                    accountID
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Users added successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} data
 * @param {*} accountID
 * @param {*} form
 * @returns
 */
export function uploadMemberBooklet(data, accountID, form) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .uploadMemberBooklet(data.classID, accountID, form)
            .then((result) => {
                if (result.ETag) {
                    dispatch({
                        type: types.EDIT_CLASS_SUCCESS,
                        data: {
                            title: data.title,
                            description: data.description,
                            classID: data.classID,
                            hasBooklet: data.hasBooklet
                        }
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Member Booklet uploaded successfully!'));
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param status
 */
export function changeBookletStatusForAccount(accountID, status) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .changeBookletStatusForAccount(accountID, status)
            .then(() => {
                dispatch(notifier.displayMessage('Member Booklet status updated successfully!'));
                window.location.reload();
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param classID
 * @param status
 */
export function changeBookletStatusForClass(classID, status) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts
            .changeBookletStatusForClass(classID, status)
            .then(() => {
                dispatch(notifier.displayMessage('Member Booklet status updated successfully!'));
                window.location.reload();
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param quoteID
 * @returns {function(*=): Promise<unknown>}
 */
export function sendMasterAppLink(accountID, quoteID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.sendMasterAppLink(accountID, quoteID)
            .then(() => {
                if (quoteID) {
                    dispatch({
                        type: quotesTypes.SET_STATE,
                        state: 7,
                        quoteID
                    });
                } else {
                    dispatch({
                        type: types.SET_STATE,
                        state: 8,
                        accountID
                    });
                }

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param effDate
 * @returns {function(*=): Promise<unknown>}
 */
export function makeAccountLive(accountID, effDate) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.makeAccountLive(accountID, effDate)
            .then(() => {
                dispatch({
                    type: types.SET_ACCOUNT_LIVE
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Account is now live!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 */
export function getRenewals(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getRenewals(accountID)
            .then((renewals) => {
                setLoader(dispatch, false);
                return renewals;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * 
 * @param {*} accountID 
 * @returns 
 */
export function getRenewalFormData(accountID, renewalID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getRenewalFormData(accountID, renewalID)
            .then((renewalFormData) => {
                setLoader(dispatch, false);
                return renewalFormData;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * 
 * @param {*} accountID 
 * @returns 
 */
export function getPreviousRenewalFormData(accountID, renewalID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getPreviousRenewalFormData(accountID, renewalID)
            .then((renewalFormData) => {
                setLoader(dispatch, false);
                return renewalFormData;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param renewalID
 * @returns {function(*): Promise<unknown>}
 */
export function completeRenewal(accountID, renewalID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.completeRenewal(accountID, renewalID)
            .then(() => {
                setLoader(dispatch, false);

                dispatch(notifier.displayMessage('Renewal completed successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param form
 * @param uid
 * @param accountID
 * @returns {function(*): Promise<unknown>}
 */
export function uploadRenewalFile(form, uid, accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.uploadRenewalFile(form, uid, accountID)
            .then((renewals) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('File uploaded successfully!'));

                return renewals;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param revisionID
 * @returns {function(*): Promise<*>}
 */
export function getRevision(revisionID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getRevision(revisionID)
            .then((data) => {
                dispatch({
                    type: types.GET_REVISION_SUCCESS,
                    data
                });

                setLoader(dispatch, false);

                return data;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
                return null;
            });
    };
}

/**
 * @param accountID
 * @param amendmentID
 * @returns {function(*): Promise<*>}
 */
export function createRevision(accountID, amendmentID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.createRevision(accountID, amendmentID)
            .then((revision) => {
                setLoader(dispatch, false);
                return revision;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param revisionID
 * @param revision
 */
export function editRevision(revisionID, revision) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.editRevision(revisionID, revision)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Revision updated successfully!'));

                setTimeout(() => {
                    window.location.reload();
                }, 500);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param revisionID
 * @param accountID
 * @returns {function(*): Promise<*>}
 */
export function completeRevisions(accountID, revisionID) {
    return (dispatch) => accounts.completeRevisions(accountID, revisionID)
        .then(() => {
            dispatch(notifier.displayMessage('Amendment Revisions completed!'));
            return true;
        })
        .catch((error) => {
            dispatch(notifier.displayError(error));
            return false;
        });
}

/**
 * @param accountID
 * @returns {function(*): *}
 */
export function getAmendments(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getAmendments(accountID)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 * @param amendmentID
 * @returns {function(*): Promise<* | boolean>}
 */
export function getAmendment(accountID, amendmentID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getAmendment(accountID, amendmentID)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 * @returns {function(*): Promise<* | boolean>}
 */
export function createAmendment(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.createAmendment(accountID)
            .then((amendmentID) => {
                setLoader(dispatch, false);
                return amendmentID;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 * @param amendmentID
 * @param amendment
 * @param skipMessage
 * @returns {function(*): Promise<boolean>}
 */
export function saveAmendment(accountID, amendmentID, amendment, skipMessage = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.saveAmendment(accountID, amendmentID, amendment)
            .then(() => {
                if (!skipMessage) {
                    setLoader(dispatch, false);
                    dispatch(notifier.displayMessage('Amendment saved successfully!'));
                }

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 * @param amendmentID
 */
export function sendAmendmentForApproval(accountID, amendmentID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.sendAmendmentForApproval(accountID, amendmentID)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Amendment successfully sent for approval!'));
                return true;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}


/**
 * @returns {function(*): (Q.Promise<boolean> | * | undefined | Promise<any>)}
 */
export function getPendingChanges(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getPendingChanges(accountID)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param changeID
 * @param accountID
 * @returns {function(*): *}
 */
export function getPendingChangeData(changeID, accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getPendingChangeData(changeID, accountID)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param changeID
 * @param accountID
 * @returns {function(*): *}
 */
export function removePendingChange(changeID, accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.removePendingChange(changeID, accountID)
            .then(() => {
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 */
export function getLsaItems(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getLsaItems(accountID)
            .then((items) => {
                setLoader(dispatch, false);
                return items;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param items
 * @returns {function(*): Promise<boolean>}
 */
export function saveLsaItems(accountID, items) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.saveLsaItems(accountID, items)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('LSA items saved successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param effectiveDate
 * @returns {function(*): Promise<*>}
 */
export function terminateAccount(accountID, effectiveDate) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.terminateAccount(accountID, effectiveDate)
            .then(() => {
                setLoader(dispatch, false);
                window.location.reload();
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param classID
 * @param divisionID
 * @param renewalID
 */
export function dispatchGetAccountNotes(accountID, classID = null, divisionID = null, getRenewalsNotes = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getAccountNotes(accountID, classID, divisionID, getRenewalsNotes)
            .then((notes) => {
                setLoader(dispatch, false);
                return notes;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 * @param classID
 * @param divisionID
 */
export function getAccountNotes(accountID, classID = null, divisionID = null) {
    return accounts.getAccountNotes(accountID, classID, divisionID)
        .then((notes) => notes)
        .catch((error) => {
            console.log(error);
            return false;
        });
}

/**
 * @param accountID
 * @param classID
 * @param divisionID
 * @param renewalID
 * @param body
 * @param expiry
 */
export function createAccountNote(accountID, classID, divisionID, renewalID, body, expiry) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.createAccountNote(accountID, classID, divisionID, renewalID, body, expiry)
            .then((note) => {
                setLoader(dispatch, false);
                return note;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param accountID
 * @param accNoteID
 */
export function removeAccountNote(accountID, accNoteID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.removeAccountNote(accountID, accNoteID)
            .then(() => {
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param advisorID
 * @returns {function(*): Promise<*>}
 */
export function getSpecialityProductMetaConfigs(advisorID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getSpecialityProductMetaConfigs(advisorID)
            .then((response) => {
                setLoader(dispatch, false);
                return response;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param advisorID
 * @param quoteID
 * @returns {function(*): Promise<*>}
 */
export function getSpecialityProductRates(accountID, advisorID, quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true, quoteID);

        return accounts.getSpecialityProductRates(accountID, advisorID, quoteID)
            .then((response) => {
                setLoader(dispatch, false, quoteID);
                return response;
            })
            .catch((error) => {
                setLoader(dispatch, false, quoteID);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param quoteID
 * @param rates
 * @param effectiveDate
 * @returns {function(*): Promise<*>}
 */
export function saveSpecialityProductRates(accountID, quoteID, rates, effectiveDate) {
    return (dispatch) => {
        setLoader(dispatch, true, quoteID);

        return accounts.saveSpecialityProductRates(accountID, quoteID, rates, effectiveDate)
            .then((response) => {
                dispatch(notifier.displayMessage('Rates saved successfully!'));
                setLoader(dispatch, false, quoteID);
                return response;
            })
            .catch((error) => {
                setLoader(dispatch, false, quoteID);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param advisorID
 * @param quoteID
 * @param rates
 * @returns {function(*): Promise<*>}
 */
export function submitSpecialityProductRates(accountID, advisorID, quoteID, rates) {
    return (dispatch) => {
        setLoader(dispatch, true, quoteID);

        return accounts.submitSpecialityProductRates(accountID, advisorID, quoteID, rates)
            .then(() => {
                setLoader(dispatch, false, quoteID);
                window.location.reload();
            })
            .catch((error) => {
                setLoader(dispatch, false, quoteID);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @returns {function(*): Promise<*>}
 */
export function getCommissionSplits(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.getCommissionSplits(accountID)
            .then((response) => {
                setLoader(dispatch, false);
                return response;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param accountID
 * @param commissionSplitData
 * @returns {function(*): Promise<*>}
 */
export function replaceCommissionSplits(accountID, commissionSplitData) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.replaceCommissionSplits(accountID, commissionSplitData)
            .then((response) => {
                setLoader(dispatch, false);
                return response;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param masterID
 * @param type
 * @param accountID
 */
export function assignRepresentative(masterID, type, accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.assignRepresentative(masterID, type, accountID)
            .then((response) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Representative assigned successfully!'));

                setTimeout(() => {
                    window.location.reload();
                }, 1000);

                return response;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param masterID
 * @param accountID
 */
export function removeRepresentatives(masterID, accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return accounts.removeRepresentative(masterID, accountID)
            .then((response) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Representative removed successfully!'));

                setTimeout(() => {
                    window.location.reload();
                }, 1000);

                return response;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}